/**
 * Política de límite de crédito en checkout (customer como medio de pago).
 * Independiente del contexto POS: entradas mínimas y salida estable para UI y tests.
 */

/** Vista mínima de cliente para validar cupo (alineable con POS `PosCheckoutCustomerOption`). */
export type CustomerCheckoutCustomerSnapshot = {
  id: string;
  name: string;
  balance: number;
  creditLimit: number;
  blocked: boolean;
};

/** Línea de cobro ya normalizada con monto numérico y medio de pago. */
export type CustomerNormalizedPaymentLineForCredit = {
  methodId: string;
  customerId?: string;
  amountNumber: number;
};

export type CustomerCreditExceededRow = {
  customer: CustomerCheckoutCustomerSnapshot;
  limit: number;
  nextBalance: number;
};

/**
 * ¿El cliente tiene tope de crédito? Modelo: `credit_limit <= 0` (o null) = sin límite.
 * Misma regla que checkout (`findCustomerCreditExceeded`), `getCustomerCreditLimitConfirmation`
 * y `CustomerCreditGauge`: una sola fuente para no contradecirse entre superficies.
 */
export function hasCustomerCreditLimit(creditLimit: number | null | undefined): boolean {
  const limit = Number(creditLimit ?? 0);
  return Number.isFinite(limit) && limit > 0;
}

/**
 * Texto único del límite para UI: 'Sin límite' cuando no hay tope, o el monto formateado.
 * Usarlo en hero de detalle, tabla y cualquier superficie que muestre el límite.
 */
export function formatCustomerCreditLimitLabel(
  creditLimit: number | null | undefined,
  formatMoney: (n: number) => string
): string {
  return hasCustomerCreditLimit(creditLimit) ? formatMoney(Number(creditLimit)) : 'Sin límite';
}

/**
 * Devuelve clientes cuyo saldo proyectado superaría el límite de crédito tras los cargos customer del borrador.
 */
export function findCustomerCreditExceeded(params: {
  normalizedPayments: CustomerNormalizedPaymentLineForCredit[];
  customerOptions: CustomerCheckoutCustomerSnapshot[];
}): CustomerCreditExceededRow[] {
  const customerDrafts = params.normalizedPayments.filter((d) => d.methodId === 'fiado' && d.customerId);
  if (customerDrafts.length === 0) {
    return [];
  }
  const byCustomer = new Map<string, number>();
  for (const d of customerDrafts) {
    const customerId = String(d.customerId);
    byCustomer.set(customerId, (byCustomer.get(customerId) ?? 0) + d.amountNumber);
  }

  const out: CustomerCreditExceededRow[] = [];
  for (const [customerId, addAmount] of byCustomer) {
    const customer = params.customerOptions.find((c) => c.id === customerId);
    if (!customer) {
      continue;
    }
    const limit = Number(customer.creditLimit ?? 0);
    // 0 o negativo = sin tope (misma regla que getCustomerCreditLimitConfirmation en movimientos manuales).
    if (limit <= 0) {
      continue;
    }
    const nextBalance = Number(customer.balance ?? 0) + addAmount;
    if (nextBalance > limit) {
      out.push({ customer, limit, nextBalance });
    }
  }
  return out;
}
